import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringOrNumberAttribute,
} from '../../../attributes';
import { Config } from '../../../../video-data/config';
import { DynamicInferredHeight, DynamicInferredWidth } from '../../../../enums';
import type { CanvasTextDynamicBoxBorderRendererSettings } from '../renderer/CanvasTextDynamicBoxBorderRenderer';
import { CanvasTextDynamicBoxBorderRenderer } from '../renderer/CanvasTextDynamicBoxBorderRenderer';
import type { LoadSettings } from '../../../serializableMixin';
import { cloneDeep } from '../../../../libs/cloneDeep';
import { TextAssetDynamicGraphic } from './TextAssetDynamicGraphic';

/**
 * TextAssetDynamicBoxBorder is the Luminary equivalent of CanvasTextDynamicBoxBorder and provides a bridge
 * between Luminary and the CanvasText library.
 */
export class TextAssetDynamicBoxBorder extends TextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicBox';

    override readonly type = Config.BOX_BORDER_GRAPHIC;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredWidth) })
    accessor boxWidth = DynamicInferredWidth.width;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredHeight) })
    accessor boxHeight = DynamicInferredHeight.maxHeight;
    @NumberAttribute({ min: 0 })
    accessor start0 = 0.25;
    @NumberAttribute({ min: 0 })
    accessor end0 = 0.75;
    @NumberAttribute({ min: 0 })
    accessor start1 = 0.75;
    @NumberAttribute({ min: 0 })
    accessor end1 = 0.25;
    @NumberAttribute()
    accessor horizontalPadding = 20;
    @NumberAttribute()
    accessor verticalPadding = 20;
    @NumberAttribute({ min: 0 })
    accessor borderThickness = 5;
    @ColorAttribute()
    accessor borderColor = 0xffffff;
    @AlphaAttribute()
    accessor borderAlpha = 0.5;

    override getCanvasRendererSettings(): CanvasTextDynamicBoxBorderRendererSettings {
        const base = super.getCanvasRendererSettings();

        const boxWidth =
            typeof this.boxWidth === 'number'
                ? this.boxWidth * this.referenceToOutputMultiplier
                : this.boxWidth;
        const boxHeight =
            typeof this.boxHeight === 'number'
                ? this.boxHeight * this.referenceToOutputMultiplier
                : this.boxHeight;

        return {
            ...base,
            type: this.type,
            boxWidth,
            boxHeight,
            start0: this.start0,
            end0: this.end0,
            start1: this.start1,
            end1: this.end1,
            horizontalPadding: this.horizontalPadding * this.referenceToOutputMultiplier,
            verticalPadding: this.verticalPadding * this.referenceToOutputMultiplier,
            borderThickness: this.borderThickness * this.referenceToOutputMultiplier,
            borderColor: this.borderColor,
            borderAlpha: this.borderAlpha,
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(getAttributesToSerialize(this));
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since CanvasTextDynamicBoxBorder is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return CanvasTextDynamicBoxBorderRenderer.animatableProperties;
    }

    override get editableProperties() {
        return super.editableProperties.concat(['borderColor', 'borderAlpha']);
    }

    override get animatableGeometricProperties() {
        return CanvasTextDynamicBoxBorderRenderer.animatableGeometricProperties;
    }

    override loadTextAssetStyle(data, settings: LoadSettings) {
        const newData = cloneDeep(data);

        if (data.shape.boxWidth !== undefined) {
            newData.boxWidth = data.shape.boxWidth;
        }
        if (data.shape.boxHeight !== undefined) {
            let boxHeight = data.shape.boxHeight;
            if (boxHeight === Config.INFERRED_HEIGHT_WITHOUT_DESCENT) {
                boxHeight = DynamicInferredHeight.heightWithoutDescent;
            }
            newData.boxHeight = boxHeight;
        }
        if (data.shape.start0 !== undefined) {
            newData.start0 = data.shape.start0;
        }
        if (data.shape.end0 !== undefined) {
            newData.end0 = data.shape.end0;
        }
        if (data.shape.start1 !== undefined) {
            newData.start1 = data.shape.start1;
        }
        if (data.shape.end1 !== undefined) {
            newData.end1 = data.shape.end1;
        }
        if (data.shape.horizontalPadding !== undefined) {
            newData.horizontalPadding = data.shape.horizontalPadding;
        }
        if (data.shape.verticalPadding !== undefined) {
            newData.verticalPadding = data.shape.verticalPadding;
        }
        if (data.shape.borderThickness !== undefined) {
            newData.borderThickness = data.shape.borderThickness;
        }
        if (data.shape.borderColor !== undefined) {
            newData.borderColor = data.shape.borderColor;
        }
        if (data.shape.borderAlpha !== undefined) {
            newData.borderAlpha = data.shape.borderAlpha;
        }

        super.loadTextAssetStyle(newData, settings);
    }
}
